/**
 * @bos/portal3 — a single project (route `/app/projects/:id`, inside <AppShell>).
 *
 * Its own addressable URL: Back / Forward / Refresh / deep-link all work because the id
 * comes from the path (useParams) and the page fetches on mount. The server resolves the
 * id owner-scoped, so a project that is missing OR belongs to someone else arrives as a
 * clean `not-found` state here — never another user's data (IDOR-safe by construction).
 *
 * M6 made the page about a BUSINESS: breadcrumbs and a project header, editable business
 * details (name / industry / description), the documents that feed it, and a quiet
 * archive control at the foot. Batch 4 replaced the old "coming soon" placeholder with
 * the REAL build door; the apps batch puts APPS in its place (apps/AppsSection), because
 * one business can have more than one: name an app and say what it should do, press Build,
 * land in the workbench — and every app the business has is listed below with its builds,
 * each with its own page. A second Build while one run is live (of ANY app) reads as
 * information, not error.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PATHS } from '../routes';
import { PageHeader } from '../components/PageHeader';
import { IndustryField } from '../projects/IndustryField';
import { useProject, projectErrorMessage, type Project } from '../projects/hooks';
import { DocumentsSection } from '../documents/DocumentsSection';
import { AppsSection } from '../apps/AppsSection';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * The editable business details. A separate component keyed on the project id in the
 * parent, so its fields seed cleanly from the loaded project (and the IndustryField's
 * list-vs-"Other" state seeds right) whenever we open a different project.
 */
function ProjectDetailsForm({
  project,
  onSave,
}: {
  project: Project;
  onSave: (fields: { name: string; description?: string; industry?: string }) => Promise<void>;
}) {
  const [name, setName] = useState(project.name);
  const [industry, setIndustry] = useState(project.industry ?? '');
  const [description, setDescription] = useState(project.description ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unchanged =
    name.trim() === project.name &&
    (description.trim() || '') === (project.description ?? '') &&
    (industry.trim() || '') === (project.industry ?? '');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await onSave({
        name,
        description: description.trim() || undefined,
        industry: industry.trim() || undefined,
      });
      setSaved(true);
    } catch (err) {
      setError(projectErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function clearSaved() {
    if (saved) setSaved(false);
  }

  return (
    <div className="card">
      <h2 className="card__title">Business details</h2>
      <p className="card__intro">
        Keep this current — it’s what Sorvel will read when you build your app.
      </p>
      <form className="form" onSubmit={onSubmit} aria-label="Business details" style={{ marginTop: '1.1rem' }}>
        {error && (
          <p className="form__error" role="alert">
            {error}
          </p>
        )}
        {saved && (
          <p className="form__success" role="status">
            Details saved.
          </p>
        )}
        <label className="form__field">
          <span className="form__label">Business name</span>
          <input
            className="form__input"
            type="text"
            name="name"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearSaved();
            }}
            disabled={busy}
          />
        </label>
        <IndustryField
          value={industry}
          onChange={(v) => {
            setIndustry(v);
            clearSaved();
          }}
          disabled={busy}
        />
        <label className="form__field">
          <span className="form__label">Description</span>
          <textarea
            className="form__textarea"
            name="description"
            placeholder="A sentence about what the business does."
            maxLength={500}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearSaved();
            }}
            disabled={busy}
          />
        </label>
        <div className="form__actions">
          <button
            className="btn btn--primary"
            type="submit"
            disabled={busy || unchanged || name.trim() === ''}
          >
            {busy ? 'Saving…' : 'Save details'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ProjectPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { project, status, error, reload, update, archive, unarchive } = useProject(id);

  // ── archive/unarchive ──
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  if (status === 'loading') {
    return (
      <section aria-busy="true">
        <p className="page__note">Loading project…</p>
      </section>
    );
  }

  if (status === 'not-found') {
    return (
      <section>
        <PageHeader
          eyebrow="Not found"
          title="Project not found"
          lede="This project doesn’t exist, or it isn’t yours. It may have been removed."
        />
        <Link className="btn btn--primary" to={PATHS.dashboard}>
          Back to your projects
        </Link>
      </section>
    );
  }

  if (status === 'error' || !project) {
    return (
      <section>
        <PageHeader eyebrow="Project" title="Something went wrong" />
        <div className="projects__state">
          <p className="form__error" role="alert">
            {error ?? 'We couldn’t load this project.'}
          </p>
          <div className="form__actions">
            <button className="btn btn--ghost" type="button" onClick={() => void reload()}>
              Try again
            </button>
            <Link className="btn btn--quiet" to={PATHS.dashboard}>
              Back to your projects
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const archived = project.status === 'archived';
  const statusBadge: ReactNode = (
    <span className={`badge badge--${archived ? 'archived' : 'active'}`}>
      {archived ? 'Archived' : 'Active'}
    </span>
  );

  async function onToggleStatus() {
    setStatusError(null);
    setStatusBusy(true);
    try {
      if (archived) await unarchive();
      else await archive();
    } catch (err) {
      setStatusError(projectErrorMessage(err));
    } finally {
      setStatusBusy(false);
    }
  }

  return (
    <section className="page--project">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to={PATHS.dashboard}>Your projects</Link>
        <span className="crumbs__sep" aria-hidden="true">
          /
        </span>
        <span className="crumbs__here">{project.name}</span>
      </nav>

      <PageHeader eyebrow="Project" title={project.name} titleAccessory={statusBadge} />
      <div className="project-meta">
        {project.industry && <span className="badge badge--industry">{project.industry}</span>}
        <span>
          Created {formatDate(project.createdAt)} · Updated {formatDate(project.updatedAt)}
        </span>
      </div>

      <div className="stack" style={{ marginTop: '1.75rem' }}>
        <AppsSection projectId={project.id} />

        <ProjectDetailsForm key={project.id} project={project} onSave={update} />

        <DocumentsSection projectId={project.id} />

        <div className="card" aria-label={archived ? 'Unarchive project' : 'Archive project'}>
          <h2 className="card__title">{archived ? 'Unarchive' : 'Archive'}</h2>
          <p className="card__intro">
            {archived
              ? 'This project is archived. Unarchive it to make it active again.'
              : 'Archiving keeps the project and its documents but marks it inactive. You can unarchive it anytime.'}
          </p>
          {statusError && (
            <p className="form__error" role="alert" style={{ marginTop: '1rem' }}>
              {statusError}
            </p>
          )}
          <div className="form__actions" style={{ marginTop: '1.1rem' }}>
            <button
              className="btn btn--ghost"
              type="button"
              onClick={() => void onToggleStatus()}
              disabled={statusBusy}
            >
              {statusBusy
                ? archived
                  ? 'Unarchiving…'
                  : 'Archiving…'
                : archived
                  ? 'Unarchive project'
                  : 'Archive project'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
